/**
 * Budgets and savings goals: the two everyday questions after "where did it
 * go?" -- "how much is left for this?" and "when will I have enough?".
 *
 * Budgets sit with the spending they limit (Income & spend), one row per
 * category against the month on screen; goals sit on the overview, counted
 * from the calendar month. The arithmetic is shared/compute.js's
 * (`budgetRows`, `goalRows`), so both work offline; changes go through the
 * queue like the rest of the ledger.
 */

import { h } from '../vdom.js';
import {
  C, MONO, css, card, input, select, button, tap, emptyLine, removeButton, monthPicker,
  expenseLabel, categoryChoices
} from './common.js';
import { numberText, parseNumber } from '../../../shared/model.js';

const GOLD = '#E3B86B';

const box = (compact, ...children) => card({
  padding: compact ? '14px 16px' : '16px 18px', display: 'flex', flexDirection: 'column', gap: compact ? '12px' : '14px',
  borderRadius: compact ? '16px' : '10px'
}, ...children);

const heading = (compact, title, sub) => h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }) },
  h('span', { style: css({ fontSize: compact ? '14px' : '15px', fontWeight: 700 }) }, title),
  sub && h('span', { key: 's', style: css({ fontSize: '12px', color: C.muted }) }, sub));

/* A bar filled to `ratio`, red past the end. */
const bar = (ratio, color, over = false) => h('div', {
  style: css({ position: 'relative', height: '8px', borderRadius: '4px', background: C.stroke, overflow: 'hidden' })
}, h('div', {
  style: css({
    position: 'absolute', left: 0, top: 0, bottom: 0, width: Math.min(100, Math.max(0, ratio * 100)) + '%',
    minWidth: ratio > 0 ? '4px' : 0, borderRadius: '4px', background: over ? C.danger : color
  })
}));

const smallButton = (label, go, key, tone = C.muted) => h('div', {
  key, ...tap(go, label), class: 'pt-button',
  style: css({
    minHeight: '32px', display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: '8px', cursor: 'pointer',
    border: `1px solid ${C.stroke}`, fontFamily: MONO, fontSize: '11px', fontWeight: 700, color: tone, whiteSpace: 'nowrap'
  })
}, label);

const field = (inputs, name, placeholder, value, { label, onEnter, width } = {}) => h('div', {
  key: name, style: css({ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, flex: width || '1 1 120px' })
},
  label && h('span', { key: 'l', style: css({ fontSize: '11px', color: C.muted }) }, label),
  input(inputs, name, placeholder, {
    type: 'number', value, ariaLabel: label || placeholder, onEnter,
    style: { height: '38px', boxSizing: 'border-box', width: '100%', fontFamily: MONO, fontSize: '13px' }
  }));

// ---------------------------------------------------------------- budgets ----

/**
 * Each budgeted category against what this month has spent on it, and a row
 * for setting another. Editing is in place: the figure becomes a box.
 */
export function budgetsCard({ state, metrics, t, fmt, inputs, actions, compact = false }) {
  const s = t.shell;
  const ui = state.budgetUi || {};
  const rows = metrics?.budgetRows || [];
  const base = state.settings.baseCurrency;
  const money = value => fmt.money(Math.round(value), 0, base);
  const taken = new Set(rows.map(row => row.key));
  const free = categoryChoices(state).filter(key => !taken.has(key));

  const save = key => {
    const amount = parseNumber(inputs.value('budget_' + key));
    if (amount === null || amount < 0) return actions.showError(s.amountNeeded);
    actions.setBudget(key, amount);
    actions.setBudgetUi({ edit: null });
  };
  const add = () => {
    const key = inputs.value('budgetNewCat') || free[0];
    const amount = parseNumber(inputs.value('budgetNewAmt'));
    if (!key) return;
    if (amount === null || amount <= 0) return actions.showError(s.amountNeeded);
    actions.setBudget(key, amount);
    inputs.clear(['budgetNewAmt']);
  };

  const over = rows.filter(row => row.over);
  return box(compact,
    heading(compact, s.bgTitle, s.bgSub(fmt.monthName(state.month))),
    !rows.length && emptyLine(s.bgEmpty),
    ...rows.map(row => {
      const editing = ui.edit === row.key;
      return h('div', { key: row.key, style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
        h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }) },
          h('span', { style: css({ width: '8px', height: '8px', borderRadius: '2px', background: row.color, flex: '0 0 8px' }) }),
          h('span', { style: css({ flex: '1 1 120px', minWidth: 0, fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) },
            expenseLabel(t, row.key)),
          editing
            ? h('div', { key: 'edit', style: css({ display: 'flex', gap: '6px', alignItems: 'center' }) },
              field(inputs, 'budget_' + row.key, '0', String(Math.round(row.limit)), { onEnter: () => save(row.key), width: '0 0 110px' }),
              smallButton(s.bgSave, () => save(row.key), 'ok', C.accent),
              smallButton('×', () => actions.setBudgetUi({ edit: null }), 'cancel'))
            : h('span', { key: 'fig', style: css({ fontFamily: MONO, fontSize: '12.5px', whiteSpace: 'nowrap', color: row.over ? C.danger : C.textDim }) },
              s.bgOf(money(row.spent), money(row.limit))),
          !editing && h('div', { key: 'acts', style: css({ display: 'flex', gap: '2px' }) },
            h('div', {
              ...tap(() => actions.setBudgetUi({ edit: row.key }), s.bgEdit), class: 'pt-button',
              style: css({ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.muted, cursor: 'pointer' })
            }, '✎'),
            removeButton(() => actions.setBudget(row.key, 0), {}, s.bgRemove))),
        bar(row.ratio, row.color, row.over),
        h('span', { style: css({ fontSize: '11.5px', color: row.over ? C.danger : row.ratio >= 0.9 ? GOLD : C.muted }) },
          row.over ? s.bgOver(money(row.spent - row.limit)) : s.bgLeft(money(row.left), Math.round(row.ratio * 100))));
    }),
    rows.length > 0 && h('div', {
      key: 'total',
      style: css({ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', paddingTop: '10px', borderTop: `1px solid ${C.line}`, fontSize: '12.5px', color: C.textDim })
    },
      h('span', null, s.bgTotal(money(rows.reduce((sum, row) => sum + row.spent, 0)), money(rows.reduce((sum, row) => sum + row.limit, 0)))),
      over.length > 0 && h('span', { key: 'o', style: css({ color: C.danger }) }, s.bgOverCount(over.length))),
    free.length > 0 && h('div', { key: 'add', style: css({ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 160px', minWidth: 0 }) },
        h('span', { style: css({ fontSize: '11px', color: C.muted }) }, s.bgCategory),
        select(inputs, 'budgetNewCat', free.map(key => ({ value: key, label: expenseLabel(t, key) })),
          { height: '38px', width: '100%' }, { ariaLabel: s.bgCategory })),
      field(inputs, 'budgetNewAmt', '0', undefined, { label: s.bgLimit(base), onEnter: add, width: '1 1 120px' }),
      button(s.bgAdd, add, { style: { height: '38px', display: 'flex', alignItems: 'center' } }))
  );
}

// ------------------------------------------------------------------ goals ----

/**
 * The goals, each with how far along it is and when it will be there; and a
 * form for a new one, or for the one being changed.
 */
export function goalsCard({ state, t, fmt, inputs, actions, compact = false }) {
  const s = t.shell;
  const ui = state.goalUi || {};
  const rows = state.metricsNow?.goalRows || [];
  const goals = state.data?.goals || [];
  const editing = ui.edit ? goals.find(goal => goal.id === ui.edit) : null;
  const money = (value, code) => fmt.money(Math.round(value), 0, code);

  const status = row => {
    if (row.done) return { text: s.glDone, color: C.accent };
    if (row.deadline && row.late) {
      return {
        text: row.needed ? s.glNeeds(money(row.needed, row.currency), fmt.monthName(row.deadline)) : s.glMissed(fmt.monthName(row.deadline)),
        color: GOLD
      };
    }
    if (row.readyMonth) {
      return {
        text: s.glReady(fmt.monthName(row.readyMonth)) + (row.onTrack ? ' · ' + s.glOnTrack(fmt.monthName(row.deadline)) : ''),
        color: row.onTrack ? C.accent : C.textDim
      };
    }
    return { text: row.needed ? s.glNeedsFrom(money(row.needed, row.currency), fmt.monthName(row.deadline)) : s.glNoPace, color: C.muted };
  };

  const save = () => {
    const name = inputs.value('goalName').trim();
    const target = parseNumber(inputs.value('goalTarget'));
    const saved = parseNumber(inputs.value('goalSaved')) || 0;
    const monthly = parseNumber(inputs.value('goalMonthly')) || 0;
    if (!name) return actions.showError(s.labelNeeded);
    if (target === null || target <= 0 || saved < 0 || monthly < 0) return actions.showError(s.amountNeeded);
    const payload = { name, target, saved, monthly, deadline: ui.deadline || null };
    if (editing) actions.updateGoal(editing.id, payload);
    else actions.addGoal(payload);
    inputs.clear(['goalName', 'goalTarget', 'goalSaved', 'goalMonthly']);
    actions.setGoalUi({ open: false, edit: null, deadline: null, rev: (ui.rev || 0) + 1 });
  };
  const open = goal => actions.setGoalUi({
    open: true, edit: goal ? goal.id : null, deadline: goal ? goal.deadline : null, rev: (ui.rev || 0) + 1
  });
  const close = () => actions.setGoalUi({ open: false, edit: null, deadline: null, rev: (ui.rev || 0) + 1 });
  const asText = value => (value ? numberText(value) : undefined);

  const form = ui.open && h('div', {
    key: 'form-' + (editing ? editing.id : 'new') + '-' + (ui.rev || 0),
    style: css({ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}` })
  },
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px' }) },
      h('span', { style: css({ fontSize: '11px', color: C.muted }) }, s.glName),
      input(inputs, 'goalName', s.glNamePh, {
        value: editing ? editing.name : undefined, ariaLabel: s.glName,
        style: { height: '38px', boxSizing: 'border-box', width: '100%', fontSize: '13px' }
      })),
    h('div', { style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap' }) },
      field(inputs, 'goalTarget', '0', editing ? asText(editing.target) : undefined, { label: s.glTarget }),
      field(inputs, 'goalSaved', '0', editing ? asText(editing.saved) : undefined, { label: s.glSaved }),
      field(inputs, 'goalMonthly', '0', editing ? asText(editing.monthly) : undefined, { label: s.glMonthly, onEnter: save })),
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }) },
      h('span', { style: css({ fontSize: '11px', color: C.muted }) }, s.glDeadline),
      monthPicker({
        id: 'goal-deadline', value: ui.deadline || '', onPick: stamp => actions.setGoalUi({ deadline: stamp }),
        label: s.glDeadline, title: s.glDeadline, note: s.glDeadlineNote, mobile: compact, state, actions, t, fmt,
        triggerStyle: { height: '36px', display: 'flex', alignItems: 'center', padding: '0 12px', border: `1px solid ${C.stroke}`, borderRadius: '8px', fontSize: '12px' }
      }),
      ui.deadline && smallButton(s.glNoDeadline, () => actions.setGoalUi({ deadline: null }), 'nodl')),
    h('div', { style: css({ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }) },
      smallButton(s.cfCancel, close, 'cancel'),
      button(editing ? s.glSaveChanges : s.glAdd, save, { style: { height: '34px', display: 'flex', alignItems: 'center' } }))
  );

  return box(compact,
    heading(compact, s.glTitle, s.glSub),
    !rows.length && !ui.open && emptyLine(s.glEmpty),
    ...rows.map(row => {
      const st = status(row);
      return h('div', { key: row.id, style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
        h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }) },
          h('span', { style: css({ flex: '1 1 140px', minWidth: 0, fontSize: '13.5px', fontWeight: 700 }) }, row.name),
          h('span', { style: css({ fontFamily: MONO, fontSize: '12.5px', color: C.textDim, whiteSpace: 'nowrap' }) },
            s.bgOf(money(row.saved, row.currency), money(row.target, row.currency)) + ' · ' + Math.round(row.ratio * 100) + '%'),
          h('div', { style: css({ display: 'flex', gap: '2px' }) },
            h('div', {
              ...tap(() => open(goals.find(goal => goal.id === row.id)), s.glEdit), class: 'pt-button',
              style: css({ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.muted, cursor: 'pointer' })
            }, '✎'),
            removeButton(() => actions.deleteGoal(row.id), {}, s.glRemove))),
        bar(row.ratio, row.done ? C.accent : C.accentHi),
        h('span', { style: css({ fontSize: '11.5px', color: st.color }) },
          st.text + (row.monthly > 0 && !row.done ? ' · ' + s.glPace(money(row.monthly, row.currency)) : '')));
    }),
    form || h('div', { key: 'open' }, smallButton(s.glNew, () => open(null), 'new', C.accent))
  );
}
